import * as ExcelJS from 'exceljs';
import { Investor, EpoRule, EpdRule } from '@/types/investor';
import { formatCurrency, formatPhone } from '@/utils/investor/validation';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { formatInvestorProducts } from '@/utils/investor/productUtils';
import { deriveDelegationLabel } from '@/utils/investor/rosterUtils';

type ChannelView = 'wholesale' | 'correspondent' | 'both';

export interface ExportData {
  investors: Investor[];
  channelView: ChannelView;
  filename?: string;
}

interface ColumnDef {
  header: string;
  key: string;
  getter: (inv: Investor) => string;
  width?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) {
return '';
}
  try {
    return parseAsLocalDate(dateStr).toLocaleDateString();
  } catch {
    return '';
  }
};

const formatTimestamp = (ts: string): string => {
  try {
    return new Date(ts).toLocaleDateString();
  } catch {
    return '';
  }
};

const formatRuleText = (
  channelRule: EpoRule | EpdRule | undefined,
  legacyRule: string | undefined
): string => {
  if (channelRule?.description) {
    return channelRule.description;
  }
  if (legacyRule) {
    return legacyRule;
  }
  return '';
};

const formatCompPlan = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return `${value}%`;
};

const formatFee = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return formatCurrency(value);
};

const obText = (
  canIntegrate: boolean | undefined,
  hasIntegrated: boolean | undefined
): string => {
  if (hasIntegrated) {
    return 'Yes';
  }
  if (canIntegrate) {
    return 'Available';
  }
  return 'No';
};

// ---------------------------------------------------------------------------
// Column definitions per channel view
// ---------------------------------------------------------------------------

const getWholesaleColumns = (): ColumnDef[] => [
  { header: 'Investor Name', key: 'name', getter: inv => inv.investor, width: 28 },
  { header: 'Product Types', key: 'products', getter: inv => formatInvestorProducts(inv, 'wholesale'), width: 30 },
  { header: 'Non-QM Doc Types', key: 'docTypes', getter: inv => inv.docType?.join(', ') || '', width: 24 },
  { header: 'Wholesale Status', key: 'wStatus', getter: inv => inv.status, width: 16 },
  { header: 'Wholesale Approved Date', key: 'wApproved', getter: inv => inv.status === 'Approved' ? formatDate(inv.approvedAsOfDate) : '', width: 22 },
  { header: 'Wholesale AE Name', key: 'wAeName', getter: inv => inv.wholesaleAeName || inv.aeName || '', width: 22 },
  { header: 'Wholesale AE Phone', key: 'wAePhone', getter: inv => formatPhone(inv.wholesaleAePhone || inv.aePhone || ''), width: 18 },
  { header: 'Wholesale AE Email', key: 'wAeEmail', getter: inv => inv.wholesaleAeEmail || inv.aeEmail || '', width: 28 },
  { header: 'Company Website', key: 'companyWebsite', getter: inv => inv.website || '', width: 30 },
  { header: 'Wholesale Website', key: 'wWebsite', getter: inv => inv.wholesaleWebsite || '', width: 30 },
  { header: 'Closing Wholesale Website', key: 'closingWWebsite', getter: inv => inv.closingWholesaleWebsite || '', width: 30 },
  { header: 'Wholesale Username', key: 'wUsername', getter: inv => inv.wholesaleUsername || inv.adminUsername || '', width: 22 },
  { header: 'Processing Credential Instructions', key: 'processingCredentialInstructions', getter: inv => inv.howToGetCredentials || '', width: 36 },
  { header: 'Processing Portal Notes', key: 'processingPortalNotes', getter: inv => inv.portalNotes || '', width: 36 },
  { header: 'Closing Credential Instructions', key: 'closingCredentialInstructions', getter: inv => inv.closingHowToGetCredentials || '', width: 36 },
  { header: 'Closing Portal Notes', key: 'closingPortalNotes', getter: inv => inv.closingPortalNotes || '', width: 36 },
  { header: 'Comp Plan (%)', key: 'compPlan', getter: inv => formatCompPlan(inv.wholesaleLenderPaidComp), width: 14 },
  { header: 'Lendwise Fee ($)', key: 'lendwiseFee', getter: inv => formatFee(inv.wholesaleAdditionalLendwiseFee), width: 16 },
  { header: 'OB Integrated', key: 'wOb', getter: inv => obText(inv.wholesaleCanIntegrateOptimalBlue, inv.wholesaleHasIntegratedOptimalBlue), width: 14 },
  { header: 'EPO Rule', key: 'wEpo', getter: inv => formatRuleText(inv.wholesaleEpoRule, inv.epoRule), width: 36 },
  { header: 'EPD Rule', key: 'wEpd', getter: inv => formatRuleText(inv.wholesaleEpdRule, inv.epdRule), width: 36 },
  { header: 'Wholesale Notes', key: 'wNotes', getter: inv => inv.wholesaleNotes || inv.notes || '', width: 30 },
  { header: 'Created Date', key: 'created', getter: inv => formatTimestamp(inv.createdAt), width: 14 },
  { header: 'Updated Date', key: 'updated', getter: inv => formatTimestamp(inv.updatedAt), width: 14 }
];

const getCorrespondentColumns = (): ColumnDef[] => [
  { header: 'Investor Name', key: 'name', getter: inv => inv.investor, width: 28 },
  { header: 'Product Types', key: 'products', getter: inv => formatInvestorProducts(inv, 'correspondent'), width: 30 },
  { header: 'Non-QM Doc Types', key: 'docTypes', getter: inv => inv.docType?.join(', ') || '', width: 24 },
  { header: 'Correspondent Status', key: 'cStatus', getter: inv => inv.correspondentStatus, width: 20 },
  { header: 'Correspondent Approved Date', key: 'cApproved', getter: inv => inv.correspondentStatus === 'Approved' ? formatDate(inv.correspondentApprovedAsOfDate) : '', width: 26 },
  { header: 'Delegation Level', key: 'delegated', getter: inv => deriveDelegationLabel(inv), width: 22 },
  { header: 'Delegated Net Worth', key: 'nwDelegated', getter: inv => inv.netWorthRequirements?.delegated ? formatCurrency(inv.netWorthRequirements.delegated) : '', width: 20 },
  { header: 'Non-Delegated Net Worth', key: 'nwNonDelegated', getter: inv => inv.netWorthRequirements?.nonDelegated ? formatCurrency(inv.netWorthRequirements.nonDelegated) : '', width: 22 },
  { header: 'Correspondent AE Name', key: 'cAeName', getter: inv => inv.correspondentAeName || inv.aeName || '', width: 22 },
  { header: 'Correspondent AE Phone', key: 'cAePhone', getter: inv => formatPhone(inv.correspondentAePhone || inv.aePhone || ''), width: 20 },
  { header: 'Correspondent AE Email', key: 'cAeEmail', getter: inv => inv.correspondentAeEmail || inv.aeEmail || '', width: 28 },
  { header: 'Company Website', key: 'companyWebsite', getter: inv => inv.website || '', width: 30 },
  { header: 'Correspondent Website', key: 'cWebsite', getter: inv => inv.correspondentWebsite || '', width: 30 },
  { header: 'Closing Correspondent Website', key: 'closingCWebsite', getter: inv => inv.closingCorrespondentWebsite || '', width: 30 },
  { header: 'Correspondent Username', key: 'cUsername', getter: inv => inv.correspondentUsername || inv.adminUsername || '', width: 22 },
  { header: 'Processing Credential Instructions', key: 'processingCredentialInstructions', getter: inv => inv.howToGetCredentials || '', width: 36 },
  { header: 'Processing Portal Notes', key: 'processingPortalNotes', getter: inv => inv.portalNotes || '', width: 36 },
  { header: 'Closing Credential Instructions', key: 'closingCredentialInstructions', getter: inv => inv.closingHowToGetCredentials || '', width: 36 },
  { header: 'Closing Portal Notes', key: 'closingPortalNotes', getter: inv => inv.closingPortalNotes || '', width: 36 },
  { header: 'OB Integrated', key: 'cOb', getter: inv => obText(inv.correspondentCanIntegrateOptimalBlue, inv.correspondentHasIntegratedOptimalBlue), width: 14 },
  { header: 'EPO Rule', key: 'cEpo', getter: inv => formatRuleText(inv.correspondentEpoRule, inv.epoRule), width: 36 },
  { header: 'EPD Rule', key: 'cEpd', getter: inv => formatRuleText(inv.correspondentEpdRule, inv.epdRule), width: 36 },
  { header: 'Correspondent Notes', key: 'cNotes', getter: inv => inv.correspondentNotes || inv.notes || '', width: 30 },
  { header: 'Created Date', key: 'created', getter: inv => formatTimestamp(inv.createdAt), width: 14 },
  { header: 'Updated Date', key: 'updated', getter: inv => formatTimestamp(inv.updatedAt), width: 14 }
];

// In "Both" view, AE/Website columns are gated by channel capability to prevent
// legacy fields from duplicating across both channels for single-channel investors.
const getBothColumns = (): ColumnDef[] => [
  { header: 'Investor Name', key: 'name', getter: inv => inv.investor, width: 28 },
  { header: 'Product Types', key: 'products', getter: inv => formatInvestorProducts(inv), width: 30 },
  { header: 'Non-QM Doc Types', key: 'docTypes', getter: inv => inv.docType?.join(', ') || '', width: 24 },
  { header: 'Wholesale Status', key: 'wStatus', getter: inv => inv.canDoWholesale ? inv.status : '', width: 16 },
  { header: 'Wholesale Approved Date', key: 'wApproved', getter: inv => inv.canDoWholesale && inv.status === 'Approved' ? formatDate(inv.approvedAsOfDate) : '', width: 22 },
  { header: 'Correspondent Status', key: 'cStatus', getter: inv => inv.canGoCorrespondent ? inv.correspondentStatus : '', width: 20 },
  { header: 'Correspondent Approved Date', key: 'cApproved', getter: inv => inv.canGoCorrespondent && inv.correspondentStatus === 'Approved' ? formatDate(inv.correspondentApprovedAsOfDate) : '', width: 26 },
  { header: 'Delegation Level', key: 'delegated', getter: inv => deriveDelegationLabel(inv), width: 22 },
  { header: 'Delegated Net Worth', key: 'nwDelegated', getter: inv => inv.netWorthRequirements?.delegated ? formatCurrency(inv.netWorthRequirements.delegated) : '', width: 20 },
  { header: 'Non-Delegated Net Worth', key: 'nwNonDelegated', getter: inv => inv.netWorthRequirements?.nonDelegated ? formatCurrency(inv.netWorthRequirements.nonDelegated) : '', width: 22 },
  // Wholesale AE — only populated when investor does wholesale
  { header: 'W AE Name', key: 'wAeName', getter: inv => inv.canDoWholesale ? (inv.wholesaleAeName || inv.aeName || '') : '', width: 20 },
  { header: 'W AE Phone', key: 'wAePhone', getter: inv => inv.canDoWholesale ? formatPhone(inv.wholesaleAePhone || inv.aePhone || '') : '', width: 16 },
  { header: 'W AE Email', key: 'wAeEmail', getter: inv => inv.canDoWholesale ? (inv.wholesaleAeEmail || inv.aeEmail || '') : '', width: 26 },
  // Correspondent AE — only populated when investor does correspondent
  { header: 'C AE Name', key: 'cAeName', getter: inv => inv.canGoCorrespondent ? (inv.correspondentAeName || inv.aeName || '') : '', width: 20 },
  { header: 'C AE Phone', key: 'cAePhone', getter: inv => inv.canGoCorrespondent ? formatPhone(inv.correspondentAePhone || inv.aePhone || '') : '', width: 16 },
  { header: 'C AE Email', key: 'cAeEmail', getter: inv => inv.canGoCorrespondent ? (inv.correspondentAeEmail || inv.aeEmail || '') : '', width: 26 },
  { header: 'Company Website', key: 'companyWebsite', getter: inv => inv.website || '', width: 30 },
  // Wholesale portal
  { header: 'W Website', key: 'wWebsite', getter: inv => inv.canDoWholesale ? (inv.wholesaleWebsite || '') : '', width: 28 },
  { header: 'Closing W Website', key: 'closingWWebsite', getter: inv => inv.canDoWholesale ? (inv.closingWholesaleWebsite || '') : '', width: 28 },
  { header: 'W Username', key: 'wUsername', getter: inv => inv.canDoWholesale ? (inv.wholesaleUsername || inv.adminUsername || '') : '', width: 20 },
  // Correspondent portal
  { header: 'C Website', key: 'cWebsite', getter: inv => inv.canGoCorrespondent ? (inv.correspondentWebsite || '') : '', width: 28 },
  { header: 'Closing C Website', key: 'closingCWebsite', getter: inv => inv.canGoCorrespondent ? (inv.closingCorrespondentWebsite || '') : '', width: 28 },
  { header: 'C Username', key: 'cUsername', getter: inv => inv.canGoCorrespondent ? (inv.correspondentUsername || inv.adminUsername || '') : '', width: 20 },
  { header: 'Processing Credential Instructions', key: 'processingCredentialInstructions', getter: inv => inv.howToGetCredentials || '', width: 36 },
  { header: 'Processing Portal Notes', key: 'processingPortalNotes', getter: inv => inv.portalNotes || '', width: 36 },
  { header: 'Closing Credential Instructions', key: 'closingCredentialInstructions', getter: inv => inv.closingHowToGetCredentials || '', width: 36 },
  { header: 'Closing Portal Notes', key: 'closingPortalNotes', getter: inv => inv.closingPortalNotes || '', width: 36 },
  // Notes — separate columns, no lossy collapse
  { header: 'Wholesale Notes', key: 'wNotes', getter: inv => inv.wholesaleNotes || '', width: 28 },
  { header: 'Correspondent Notes', key: 'cNotes', getter: inv => inv.correspondentNotes || '', width: 28 },
  { header: 'Created Date', key: 'created', getter: inv => formatTimestamp(inv.createdAt), width: 14 },
  { header: 'Updated Date', key: 'updated', getter: inv => formatTimestamp(inv.updatedAt), width: 14 }
];

const getColumnsForChannel = (channelView: ChannelView): ColumnDef[] => {
  switch (channelView) {
    case 'wholesale': return getWholesaleColumns();
    case 'correspondent': return getCorrespondentColumns();
    case 'both': return getBothColumns();
  }
};

// ---------------------------------------------------------------------------
// CSV Export
// ---------------------------------------------------------------------------

export const exportToCSV = ({ investors, channelView, filename = 'investors' }: ExportData): void => {
  const columns = getColumnsForChannel(channelView);
  const headers = columns.map(c => c.header);

  const csvData = investors.map(investor =>
    columns.map(col => col.getter(investor))
  );

  const csvContent = [headers, ...csvData]
    .map(row => row.map(field => `"${String(field ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');

  downloadFile(csvContent, `${filename}.csv`, 'text/csv');
};

// ---------------------------------------------------------------------------
// Excel Export
// ---------------------------------------------------------------------------

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF333333' }
};

const HEADER_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  color: { argb: 'FFFFFFFF' },
  size: 10
};

const channelLabel = (view: ChannelView): string => {
  switch (view) {
    case 'wholesale': return 'Wholesale';
    case 'correspondent': return 'Correspondent';
    case 'both': return 'All Channels';
  }
};

export const exportToExcel = async ({ investors, channelView, filename = 'investors' }: ExportData): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'LendWise';
  workbook.created = new Date();

  const columns = getColumnsForChannel(channelView);

  // -- Investors sheet --
  const mainSheet = workbook.addWorksheet('Investors');
  mainSheet.columns = columns.map(col => ({
    header: col.header,
    key: col.key,
    width: col.width ?? 18
  }));

  // Style header row
  const headerRow = mainSheet.getRow(1);
  headerRow.eachCell(cell => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });
  headerRow.height = 24;

  // Add data
  for (const investor of investors) {
    const row: Record<string, string> = {};
    for (const col of columns) {
      row[col.key] = col.getter(investor);
    }
    mainSheet.addRow(row);
  }

  // Auto-size columns (measure actual content, cap at defined width)
  mainSheet.columns.forEach(col => {
    let maxLen = String(col.header ?? '').length;
    col.eachCell?.({ includeEmpty: false }, cell => {
      const len = String(cell.value ?? '').length;
      if (len > maxLen) {
maxLen = len;
}
    });
    col.width = Math.min(maxLen + 2, (col.width as number) ?? 50);
  });

  // -- Summary sheet --
  const summarySheet = workbook.addWorksheet('Summary');
  summarySheet.columns = [
    { header: 'Category', key: 'category', width: 30 },
    { header: 'Count', key: 'count', width: 12 }
  ];

  const summaryHeaderRow = summarySheet.getRow(1);
  summaryHeaderRow.eachCell(cell => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
  });

  const summaryRows: [string, string | number][] = [
    ['Channel View', channelLabel(channelView)],
    ['Total Investors', investors.length],
    ['Exported', new Date().toLocaleString()],
    ['', '']
  ];

  if (channelView === 'wholesale' || channelView === 'both') {
    const wholesaleBreakdown = investors.reduce((acc, inv) => {
      if (inv.canDoWholesale) {
acc[inv.status] = (acc[inv.status] || 0) + 1;
}
      return acc;
    }, {} as Record<string, number>);
    summaryRows.push(['Wholesale Status', '']);
    for (const [status, count] of Object.entries(wholesaleBreakdown)) {
      summaryRows.push([`  ${status}`, count]);
    }
    summaryRows.push(['', '']);
  }

  if (channelView === 'correspondent' || channelView === 'both') {
    const corrBreakdown = investors.reduce((acc, inv) => {
      if (inv.canGoCorrespondent) {
acc[inv.correspondentStatus] = (acc[inv.correspondentStatus] || 0) + 1;
}
      return acc;
    }, {} as Record<string, number>);
    summaryRows.push(['Correspondent Status', '']);
    for (const [status, count] of Object.entries(corrBreakdown)) {
      summaryRows.push([`  ${status}`, count]);
    }

    const delegatedBreakdown = investors.reduce((acc, inv) => {
      if (inv.canGoCorrespondent) {
        const label = deriveDelegationLabel(inv);
        acc[label] = (acc[label] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
    summaryRows.push(['', '']);
    summaryRows.push(['Delegation Type', '']);
    for (const [type, count] of Object.entries(delegatedBreakdown)) {
      summaryRows.push([`  ${type}`, count]);
    }
  }

  for (const [category, count] of summaryRows) {
    summarySheet.addRow({ category, count });
  }

  // Generate and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generateExportFilename = (
  prefix: string = 'investors',
  channelView?: ChannelView
): string => {
  const now = new Date();
  const timestamp = now.toISOString()
    .replace(/[:.]/g, '-')
    .replace('T', '_')
    .split('.')[0];
  const channelSuffix = channelView && channelView !== 'both' ? `_${channelView}` : '';
  return `${prefix}${channelSuffix}_${timestamp}`;
};

// Export filtered data based on current filters and channel view
export const exportFilteredData = async (
  investors: Investor[],
  format: 'csv' | 'excel',
  channelView: ChannelView = 'both'
): Promise<void> => {
  const filename = generateExportFilename('investors', channelView);

  if (format === 'csv') {
    exportToCSV({ investors, channelView, filename });
  } else {
    await exportToExcel({ investors, channelView, filename });
  }
};
